// externals
import type { ReactElement } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// libraries
import { Action } from '@interstate/components/Action';
import { Alert } from '@interstate/components/Alert';
import { Typography } from '@interstate/components/Typography';

// actions
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';

// selectors
import * as lenderAddressSelectors from '@makemydeal/dr-dash-store/selectors/lenderAddressSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';

// consts/enums
import {
    CREDIT_DECISION_CONTRACTING_LENDER_NAME,
    LENDER_ADDRESS_ERROR_ALERT_BODY_PREFIX,
    LENDER_ADDRESS_ERROR_ALERT_BODY_SUFFIX,
    LENDER_ADDRESS_ERROR_ALERT_TITLE
} from '../constants';

export const LenderAddressErrorAlert = (): ReactElement | null => {
    const dispatch = useDispatch();
    const hasLenderAddressError = useSelector(lenderAddressSelectors.getHasLenderAddressError);
    const lender = useSelector(offerReduxSelectors.getCreditDecisionLender);

    const handleContractingLenderClick = (): void => {
        dispatch(interactionAnalyticsActionCreators.mvLenderLink());
        dispatch(baseDealActionCreators.openSlideOut(baseDealActionCreators.SlideOutType.Lender));
    };

    if (!hasLenderAddressError || !lender?.id) {
        return null;
    }

    return (
        <Alert
            type="error"
            role="alert"
            title={LENDER_ADDRESS_ERROR_ALERT_TITLE}
            data-testid="lender-address-error-alert"
            sx={{ marginBottom: 2, marginTop: 2 }}
        >
            <Typography variant="body-sm" tag="span">
                {LENDER_ADDRESS_ERROR_ALERT_BODY_PREFIX}
            </Typography>
            <Action onClick={handleContractingLenderClick} data-testid="lender-address-error-alert-cta">
                <Typography variant="anchor-inline-sm" tag="a" color="sem.color.surface.button.primary.rest">
                    {CREDIT_DECISION_CONTRACTING_LENDER_NAME}
                </Typography>
            </Action>
            <Typography variant="body-sm" tag="span">
                {LENDER_ADDRESS_ERROR_ALERT_BODY_SUFFIX}
            </Typography>
        </Alert>
    );
};
